import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc, doc,
  serverTimestamp, query, where, orderBy, getDoc,
} from 'firebase/firestore';

const PLATFORMS = ['Claude', 'ChatGPT', 'Copilot', 'Gemini', 'Zapier', 'Other'];

const EMPTY_FORM = { name: '', purpose: '', platform: 'Claude', instructions: '', link: '', tips: '' };

// Company-wide library of the agents people have built. Everyone in the company
// can see and export every agent (like the Wins Wall); each person can edit or
// delete their own entries. mode: 'member' (uses the signed-in user's company)
// or 'admin' (TCF admins pick a program code to view).
export default function AgentLibrary({ user, mode = 'member' }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [companyName, setCompanyName] = useState('');

  // Admin mode: code picker
  const [codes, setCodes] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        if (mode === 'admin') {
          const snap = await getDocs(collection(db, 'engagements'));
          setCodes([...new Set(snap.docs.map(d => d.data().company).filter(Boolean))].sort());
        } else {
          const profileSnap = await getDoc(doc(db, 'users', user.uid));
          if (profileSnap.exists()) {
            setUserProfile(profileSnap.data());
            const name = profileSnap.data().company || '';
            setCompanyName(name);
            if (name) await loadAgents(name);
          }
        }
      } catch { /* shown as empty state */ }
      setLoading(false);
    };
    init();
  }, [user.uid, mode]);

  const loadAgents = async (name) => {
    if (!name) { setAgents([]); return; }
    try {
      const q = query(collection(db, 'agentLibrary'), where('company', '==', name), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setAgents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      const snap = await getDocs(query(collection(db, 'agentLibrary'), where('company', '==', name)));
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setAgents(docs);
    }
  };

  const showMsg_ = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.purpose.trim()) {
      showMsg_('error', 'Give your agent a name and say what it does.');
      return;
    }
    if (!companyName) {
      showMsg_('error', 'Your account is not linked to a company. Contact your program coordinator.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        purpose: form.purpose.trim(),
        platform: form.platform,
        instructions: form.instructions.trim(),
        link: form.link.trim(),
        tips: form.tips.trim(),
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'agentLibrary', editingId), payload);
        showMsg_('success', 'Agent updated.');
      } else {
        await addDoc(collection(db, 'agentLibrary'), {
          ...payload,
          authorId: user.uid,
          authorName: userProfile?.displayName || user.displayName || 'Team Member',
          companyCode: userProfile?.companyCode || '',
          company: companyName,
          createdAt: serverTimestamp(),
        });
        showMsg_('success', 'Agent shared with your company.');
      }
      await loadAgents(companyName);
      resetForm();
    } catch {
      showMsg_('error', 'Could not save. Please try again.');
    }
    setSaving(false);
  };

  const handleEdit = (a) => {
    setForm({
      name: a.name || '', purpose: a.purpose || '', platform: a.platform || 'Other',
      instructions: a.instructions || '', link: a.link || '', tips: a.tips || '',
    });
    setEditingId(a.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This removes it for your whole company.`)) return;
    try {
      await deleteDoc(doc(db, 'agentLibrary', a.id));
      setAgents(prev => prev.filter(x => x.id !== a.id));
    } catch {
      showMsg_('error', 'Could not delete. Please try again.');
    }
  };

  const handleCopy = async (a) => {
    try {
      await navigator.clipboard.writeText(a.instructions || '');
      setCopiedId(a.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const handleExport = async () => {
    const XLSX = await import('xlsx');
    const rows = shownAgents.map(a => ({
      'Agent name': a.name || '',
      'Platform': a.platform || '',
      'What it does': a.purpose || '',
      'Setup instructions': a.instructions || '',
      'Link': a.link || '',
      'Tips': a.tips || '',
      'Shared by': a.authorName || '',
      'Date': a.createdAt?.toDate ? a.createdAt.toDate().toLocaleDateString('en-US') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 28 }, { wch: 12 }, { wch: 40 }, { wch: 60 }, { wch: 30 }, { wch: 40 }, { wch: 20 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Agent Library');
    XLSX.writeFile(wb, `agent-library-${(companyName || 'export').replace(/[^a-zA-Z0-9-]/g, '_')}.xlsx`);
  };

  const formatDate = (ts) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const shownAgents = search.trim()
    ? agents.filter(a =>
        [a.name, a.purpose, a.platform, a.tips, a.authorName]
          .join(' ').toLowerCase().includes(search.trim().toLowerCase()))
    : agents;

  if (loading) return <div style={s.loading}>Loading agent library…</div>;

  return (
    <div style={s.container}>
      <div style={s.headerBlock}>
        <h1 style={s.heading}>Agent Library</h1>
        <p style={s.sub}>
          The agents your company has built, all in one place. Browse what your teammates made, copy the setup
          instructions to build your own version, and share the agents you design so everyone benefits.
        </p>
      </div>

      {mode === 'admin' && (
        <div style={s.card}>
          <label style={s.label}>Company</label>
          <select
            style={{ ...s.input, maxWidth: '340px' }}
            value={companyName}
            onChange={async (e) => { setCompanyName(e.target.value); await loadAgents(e.target.value); }}
          >
            <option value="">Choose a company…</option>
            {codes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {mode === 'member' && !companyName && (
        <div style={s.empty}>Your account is not linked to a company code. Contact your program coordinator to get access to the Agent Library.</div>
      )}

      {mode === 'member' && companyName && (
        <>
          {!showForm && (
            <button style={s.shareBtn} onClick={() => setShowForm(true)}>+ Share an agent</button>
          )}

          {showForm && (
            <form onSubmit={handleSave} style={s.formCard}>
              <div style={s.formTitle}>{editingId ? 'Edit your agent' : 'Share an agent'}</div>

              <label style={s.label}>Agent name</label>
              <input style={s.input} type="text" placeholder="Meeting Prep Agent" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={80} />

              <label style={s.label}>What it does</label>
              <textarea style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} placeholder="One or two sentences: what does this agent handle for you, and when do you use it?" value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} maxLength={400} />

              <label style={s.label}>Platform it runs on</label>
              <select style={{ ...s.input, maxWidth: '240px' }} value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>

              <label style={s.label}>Setup instructions (so a teammate can rebuild it)</label>
              <textarea style={{ ...s.input, minHeight: '110px', resize: 'vertical' }} placeholder="Paste the agent's instructions or system prompt here. This is the part teammates copy to build their own version." value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} />

              <label style={s.label}>Link (optional)</label>
              <input style={s.input} type="text" placeholder="A link to the agent itself, if your platform shares one" value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} />

              <label style={s.label}>Tips (optional)</label>
              <textarea style={{ ...s.input, minHeight: '50px', resize: 'vertical' }} placeholder="Anything a teammate should know: what it is best at, what to watch out for." value={form.tips} onChange={e => setForm(f => ({ ...f, tips: e.target.value }))} maxLength={400} />

              <div style={s.formActions}>
                <button type="submit" style={s.saveBtn} disabled={saving}>{saving ? 'Saving…' : (editingId ? 'Save changes' : 'Share agent')}</button>
                <button type="button" style={s.cancelBtn} onClick={resetForm}>Cancel</button>
              </div>
            </form>
          )}
        </>
      )}

      {msg && <div style={{ ...s.msgBox, ...(msg.type === 'error' ? s.msgError : s.msgSuccess) }}>{msg.text}</div>}

      {companyName && (
        <div style={s.toolbar}>
          <div style={s.count}>{shownAgents.length} agent{shownAgents.length === 1 ? '' : 's'}{search.trim() ? ' found' : ' shared'}</div>
          <div style={s.toolbarRight}>
            {agents.length > 0 && (
              <input style={s.searchInput} placeholder="Search agents…" value={search} onChange={e => setSearch(e.target.value)} />
            )}
            {shownAgents.length > 0 && (
              <button style={s.exportBtn} onClick={handleExport}>Export to Excel</button>
            )}
          </div>
        </div>
      )}

      {companyName && agents.length === 0 && (
        <div style={s.empty}>
          No agents shared yet.{mode === 'member' ? ' Built one during the program? Be the first to share it.' : ''}
        </div>
      )}

      <div style={s.grid}>
        {shownAgents.map(a => (
          <div key={a.id} style={s.agentCard}>
            <div style={s.cardHead}>
              <span style={s.platformTag}>{a.platform || 'Agent'}</span>
              <span style={s.cardMeta}>{a.authorName}{a.createdAt ? ` · ${formatDate(a.createdAt)}` : ''}</span>
            </div>
            <div style={s.cardTitle}>{a.name}</div>
            <div style={s.cardPurpose}>{a.purpose}</div>

            {a.tips && <div style={s.tipsBox}><strong>Tips:</strong> {a.tips}</div>}

            {a.link && (
              <a href={a.link.startsWith('http') ? a.link : `https://${a.link}`} target="_blank" rel="noopener noreferrer" style={s.link}>
                Open the agent ↗
              </a>
            )}

            {a.instructions && (
              <>
                <button style={s.toggleBtn} onClick={() => setExpanded(prev => ({ ...prev, [a.id]: !prev[a.id] }))}>
                  {expanded[a.id] ? 'Hide setup instructions' : 'Show setup instructions'}
                </button>
                {expanded[a.id] && (
                  <div style={s.instructionsBox}>
                    <pre style={s.instructionsText}>{a.instructions}</pre>
                    <button style={s.copyBtn} onClick={() => handleCopy(a)}>
                      {copiedId === a.id ? '✓ Copied' : 'Copy instructions'}
                    </button>
                  </div>
                )}
              </>
            )}

            {mode === 'member' && a.authorId === user.uid && (
              <div style={s.ownerRow}>
                <button style={s.editBtn} onClick={() => handleEdit(a)}>Edit</button>
                <button style={s.deleteBtn} onClick={() => handleDelete(a)}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const N = '#1B2A4A';
const s = {
  container: { padding: '24px 32px', maxWidth: '900px' },
  loading: { padding: '40px', textAlign: 'center', color: '#3D3D3D', fontFamily: 'DM Sans, sans-serif' },
  headerBlock: { marginBottom: '20px' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: N, marginBottom: '8px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.6', maxWidth: '700px', fontFamily: 'DM Sans, sans-serif' },
  card: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '18px 20px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: N, letterSpacing: '0.4px', textTransform: 'uppercase', margin: '12px 0 5px', fontFamily: 'DM Sans, sans-serif' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  shareBtn: { background: N, color: '#fff', border: 'none', padding: '12px 26px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginBottom: '20px' },
  formCard: { background: '#FFFFFF', border: '1.5px solid #D4A847', padding: '22px 24px', marginBottom: '24px' },
  formTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: N, marginBottom: '6px' },
  formActions: { display: 'flex', gap: '10px', marginTop: '16px' },
  saveBtn: { background: N, color: '#fff', border: 'none', padding: '11px 24px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  cancelBtn: { background: '#F2F0EC', color: N, border: '1.5px solid #E8E6E2', padding: '11px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  msgBox: { fontSize: '13px', padding: '10px 14px', marginBottom: '16px', fontFamily: 'DM Sans, sans-serif' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
  count: { fontSize: '12px', color: '#8A8A8A', fontFamily: 'DM Sans, sans-serif' },
  toolbarRight: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  searchInput: { border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#fff', outline: 'none', minWidth: '170px' },
  exportBtn: { background: '#F2F0EC', color: N, border: '1.5px solid #E8E6E2', padding: '9px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  empty: { padding: '24px', background: '#F2F0EC', color: '#3D3D3D', fontSize: '13px', fontFamily: 'DM Sans, sans-serif', marginBottom: '16px' },
  grid: { display: 'flex', flexDirection: 'column', gap: '16px' },
  agentCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 22px' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' },
  platformTag: { display: 'inline-block', background: 'rgba(212,168,71,0.18)', color: '#8A6F2A', fontSize: '10px', fontWeight: '700', padding: '3px 9px', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'DM Sans, sans-serif' },
  cardMeta: { fontSize: '11px', color: '#8A8A8A', fontFamily: 'DM Sans, sans-serif' },
  cardTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: N, marginBottom: '6px' },
  cardPurpose: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6', fontWeight: '300', fontFamily: 'DM Sans, sans-serif', marginBottom: '10px' },
  tipsBox: { fontSize: '12.5px', color: '#3D3D3D', background: '#FAF8F5', border: '1px solid #E8E6E2', padding: '10px 12px', lineHeight: '1.5', fontFamily: 'DM Sans, sans-serif', marginBottom: '10px' },
  link: { display: 'inline-block', fontSize: '12px', color: '#185fa5', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif', marginBottom: '10px' },
  toggleBtn: { display: 'block', background: 'none', border: 'none', padding: 0, color: '#185fa5', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline', marginBottom: '8px' },
  instructionsBox: { background: '#FAF8F5', border: '1px solid #E8E6E2', padding: '12px 14px', marginBottom: '10px' },
  instructionsText: { margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'Consolas, Menlo, monospace', fontSize: '12px', color: '#3D3D3D', lineHeight: '1.55' },
  copyBtn: { marginTop: '10px', background: N, color: '#fff', border: 'none', padding: '8px 16px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  ownerRow: { display: 'flex', gap: '8px', marginTop: '10px', paddingTop: '12px', borderTop: '1px solid #F2F0EC' },
  editBtn: { background: '#F2F0EC', color: N, border: '1.5px solid #E8E6E2', padding: '7px 14px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  deleteBtn: { background: '#fff', color: '#a32d2d', border: '1.5px solid #e6c4c4', padding: '7px 14px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
};
